import { Injectable } from '@angular/core';
import { MwHttpService } from '@core/http';
import { Observable } from 'rxjs';
import {
  ExportBatchApiModel,
  ExportBatchDeliveryFilterModel,
} from 'app/models/export-batch-api.model';
import {
  DeliveryExportStatusCountModel,
  DeliveryExportStatusEnum,
} from 'app/models/delivery-api.model';
import { ReportStatus } from 'app/models/report-status.enum';
import { ReportApiService } from 'app/services/report-api.service';
import { ReportGenerateRequest } from 'app/models/report-generate-request';
import { switchMap } from 'rxjs/operators';
import { environment } from 'environments/environment';

@Injectable()
export class MwExportBatchesApiService {
  constructor(
    private http: MwHttpService,
    private reportApiService: ReportApiService
  ) {}

  private apiEndpoint(version = 1): string {
    return `/transport/v${version}/Companies/{companyId}/ExportBatches`;
  }

  getById(id: number): Observable<ExportBatchApiModel> {
    return this.http.authGet(`${this.apiEndpoint()}/${id}`);
  }

  getStatusCount(
    filter: ExportBatchDeliveryFilterModel
  ): Observable<DeliveryExportStatusCountModel[]> {
    var filters =
      `?startDate=${filter.startDate?.toISOString()}&endDate=${filter.endDate?.toISOString()}` +
      `&customerId=${filter.customerId}&customerInternalProjectId=${filter.customerProjectId}` +
      `&supplierId=${filter.supplierId}`;

    return this.http.authGet(
      `${this.apiEndpoint()}/ExportStatusCount` + filters
    );
  }

  load(): Observable<ExportBatchApiModel[]> {
    return this.http.authGet<ExportBatchApiModel[]>(this.apiEndpoint(2));
  }

  create(model: any): Observable<ExportBatchApiModel> {
    const apiModel = this.toApiModel(model);
    return this.http.authPost<ExportBatchApiModel>(this.apiEndpoint(), {
      body: apiModel,
    });
  }

  update(model: ExportBatchApiModel): Observable<any> {
    return this.http.authPut<ExportBatchApiModel>(this.apiEndpoint(), {
      body: model,
    });
  }

  export(model: ExportBatchApiModel): Observable<any> {
    return this.http.authPut<ExportBatchApiModel>(
      `${this.apiEndpoint()}/Export`,
      {
        body: model,
      }
    );
  }

  delete(model: ExportBatchApiModel[] | ExportBatchApiModel): Observable<any> {
    if (!(model instanceof Array)) {
      model = [model];
    }
    return this.http.authDelete<ExportBatchApiModel[]>(
      `${this.apiEndpoint()}/many`,
      {
        body: model,
      }
    );
  }

  generateReport(uid: string, exportBatchId: number): Observable<Blob> {
    const getRequest = (): ReportGenerateRequest =>
      this.getReportGenerateRequest({
        uid,
        exportBatchId,
      });
    return this.reportApiService.generateReport(getRequest());
  }

  getReportPath(uid: string): string {
    return `exportbatch/${uid}`;
  }

  private getReportGenerateRequest(data: {
    uid: string;
    exportBatchId: number;
  }): ReportGenerateRequest {
    return {
      token: data.uid,
      parameters: {
        ExportBatchId: data.exportBatchId,
        Format: 'XLSX',
      },
      path: this.getReportPath(data.uid),
      reportLayoutId: environment.exportBatchLayoutId,
    };
  }

  private toApiModel(model: any): ExportBatchApiModel {
    const apiModel: ExportBatchApiModel = { ...model };
    apiModel.exportStatuses = [];
    Object.values(DeliveryExportStatusEnum).forEach((status) => {
      if (model[`exportStatus_${status}`]) {
        apiModel.exportStatuses.push(status);
      }
      delete apiModel[`exportStatus_${status}`];
    });
    return apiModel;
  }
}
